import { useRef, useEffect, useState } from 'react';
import { Brain, Mic, Sparkles, AlertCircle, Zap, ShieldCheck, BarChart2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { t } from '@/i18n/translations';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { KpiStrip } from '@/components/KpiStrip';

const CHAT_MSGS = {
  en: [
    { role:'ai',   text:"Good afternoon! Time for your end-of-shift debrief. How did it go today?" },
    { role:'user', text:"Caffe Bar LJ – too noisy, people weren't interested. Concert was going on." },
    { role:'ai',   text:"Logged as 'External Disruption'. Caffe Bar LJ moved to morning slot next week. ✓" },
  ],
  sl: [
    { role:'ai',   text:"Dober dan! Čas je za zaključni debrief izmene. Kako je šlo danes?" },
    { role:'user', text:"Caffe Bar LJ – preveč hrupno, ljudje niso bili zainteresirani. Potekal je koncert." },
    { role:'ai',   text:"Zabeleženo kot 'Zunanja motnja'. Caffe Bar LJ premaknjen na jutranji termin naslednji teden. ✓" },
  ],
};

const INSIGHT_LABEL = { en: 'INSIGHT', sl: 'VPGLED' };
const INSIGHT_TEXT  = { en: 'External Disruption · Caffe Bar LJ', sl: 'Zunanja motnja · Caffe Bar LJ' };

// ─── Mini AI Chat preview inside iPad ────────────────────────────────────────
const AiChatScreen = ({ visible, lang }) => {
  const [step, setStep] = useState(0);
  const MSGS = CHAT_MSGS[lang] || CHAT_MSGS.en;

  useEffect(() => {
    setStep(0);
    if (!visible) return;
    let i = 0;
    const run = () => {
      if (i < MSGS.length) { setStep(s => s + 1); i++; setTimeout(run, 1400); }
    };
    setTimeout(run, 600);
  }, [visible, lang]);

  return (
    <div className="h-full flex flex-col" style={{ background:'#0A111F', fontFamily:'monospace' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 flex-shrink-0"
        style={{ background:'rgba(8,15,30,0.95)', borderBottom:'1px solid rgba(255,255,255,0.07)' }}>
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-5 rounded-lg flex items-center justify-center"
            style={{ background:'rgba(240,141,73,0.2)', border:'1px solid rgba(240,141,73,0.3)' }}>
            <Brain className="w-2.5 h-2.5" style={{ color:'#F08D49' }}/>
          </div>
          <span style={{ fontSize:8, fontWeight:700, color:'rgba(255,255,255,0.85)', letterSpacing:'0.08em' }}>AI DEBRIEF</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" style={{ boxShadow:'0 0 4px #34D399' }}/>
          <span style={{ fontSize:7, color:'#34D399', letterSpacing:'0.1em' }}>LIVE</span>
        </div>
      </div>

      {/* Chat */}
      <div className="flex-1 px-3 py-2 space-y-2 overflow-hidden">
        {MSGS.slice(0, step).map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            style={{ animation:'fadeUp 0.3s ease forwards' }}>
            {msg.role === 'ai' && (
              <div className="w-4 h-4 rounded-full flex items-center justify-center mr-1 mt-0.5 flex-shrink-0"
                style={{ background:'rgba(74,144,226,0.2)', border:'1px solid rgba(74,144,226,0.3)' }}>
                <Brain style={{ width:8, height:8, color:'#4A90E2' }}/>
              </div>
            )}
            <div className="px-2.5 py-1.5 rounded-xl max-w-[78%]"
              style={{
                fontSize:8, lineHeight:'1.5', letterSpacing:'0.01em',
                background: msg.role === 'ai' ? 'rgba(74,144,226,0.1)' : 'rgba(240,141,73,0.12)',
                border: msg.role === 'ai' ? '1px solid rgba(74,144,226,0.2)' : '1px solid rgba(240,141,73,0.2)',
                color:'rgba(255,255,255,0.85)',
                borderRadius: msg.role === 'ai' ? '3px 10px 10px 10px' : '10px 3px 10px 10px',
              }}>
              {msg.text}
            </div>
          </div>
        ))}

        {/* Insight card */}
        {step >= 2 && (
          <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg"
            style={{ background:'rgba(251,191,36,0.08)', border:'1px solid rgba(251,191,36,0.25)',
              animation:'insightPop 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards' }}>
            <AlertCircle style={{ width:8, height:8, color:'#FBD038', flexShrink:0 }}/>
            <span style={{ fontSize:7, color:'rgba(255,255,255,0.7)', letterSpacing:'0.01em' }}>
              <span style={{ color:'#FBD038' }}>{INSIGHT_LABEL[lang] || 'INSIGHT'}:</span> {INSIGHT_TEXT[lang] || INSIGHT_TEXT.en}
            </span>
          </div>
        )}
      </div>

      {/* Mic button */}
      <div className="flex flex-col items-center gap-1.5 px-3 py-2.5 flex-shrink-0"
        style={{ borderTop:'1px solid rgba(255,255,255,0.06)' }}>
        {/* Waveform */}
        <div className="flex items-center gap-0.5 h-4">
          {Array.from({length:16}).map((_,i) => (
            <div key={i} className="w-0.5 rounded-full"
              style={{ background:'rgba(240,141,73,0.35)',
                height:`${4 + Math.abs(Math.sin(i*0.8))*8}px`,
                animation:`wv ${0.5+i*0.07}s ease-in-out infinite alternate`,
                animationDelay:`${i*0.05}s` }}/>
          ))}
        </div>
        <div className="w-8 h-8 rounded-full flex items-center justify-center"
          style={{ background:'linear-gradient(135deg,#F08D49,#e07030)', boxShadow:'0 0 12px rgba(240,141,73,0.4)' }}>
          <Mic style={{ width:12, height:12, color:'white' }}/>
        </div>
        <span style={{ fontSize:7, color:'rgba(255,255,255,0.3)', letterSpacing:'0.08em' }}>TAP TO SPEAK</span>
      </div>

      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes insightPop { from{opacity:0;transform:scale(0.9)} to{opacity:1;transform:scale(1)} }
        @keyframes wv { from{transform:scaleY(0.4)} to{transform:scaleY(1)} }
      `}</style>
    </div>
  );
};

// ─── iPad frame (identical to IntelSection) ───────────────────────────────────
const IPad = ({ visible, lang }) => (
  <div className="relative mx-auto select-none" style={{ width:'100%', maxWidth:380 }}>
    <div className="absolute -inset-6 rounded-[50px] blur-3xl pointer-events-none"
      style={{ background:'radial-gradient(ellipse, rgba(240,141,73,0.12) 0%, transparent 70%)' }}/>
    <div className="relative rounded-[32px] shadow-[0_30px_80px_rgba(0,0,0,0.7),0_0_0_1px_rgba(255,255,255,0.08)]"
      style={{ background:'linear-gradient(160deg,#2a2a2e 0%,#1c1c1e 40%,#141416 100%)', padding:'14px 12px 18px' }}>
      <div className="flex items-center justify-center mb-1.5" style={{ height:18 }}>
        <div className="w-2 h-2 rounded-full" style={{ background:'#0a0a0c', border:'1px solid rgba(255,255,255,0.06)' }}>
          <div className="w-1 h-1 rounded-full mx-auto mt-0.5" style={{ background:'rgba(56,189,248,0.3)' }}/>
        </div>
      </div>
      <div className="rounded-2xl overflow-hidden relative" style={{ aspectRatio:'3/4.2', boxShadow:'inset 0 0 0 1px rgba(255,255,255,0.05)' }}>
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-3 py-1"
          style={{ background:'rgba(8,15,30,0.9)', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
          <span style={{ fontSize:8, color:'rgba(255,255,255,0.5)', fontFamily:'monospace' }}>9:41</span>
          <div className="flex items-center gap-1">
            {[3,4,5,6].map(h=>(
              <div key={h} className="w-0.5 rounded-sm" style={{ height:h, background:'rgba(255,255,255,0.5)' }}/>
            ))}
            <div className="w-4 h-2 rounded-sm ml-1 relative" style={{ border:'1px solid rgba(255,255,255,0.35)' }}>
              <div className="absolute inset-0.5 rounded-sm" style={{ background:'rgba(255,255,255,0.6)', width:'70%' }}/>
            </div>
          </div>
        </div>
        <div className="h-full" style={{ paddingTop:18 }}>
          <AiChatScreen visible={visible} lang={lang}/>
        </div>
      </div>
      <div className="flex justify-center mt-2.5">
        <div className="w-16 h-1 rounded-full" style={{ background:'rgba(255,255,255,0.15)' }}/>
      </div>
    </div>
    <div className="absolute right-0 top-24 w-0.5 h-10 rounded-l-sm" style={{ background:'rgba(255,255,255,0.12)', right:-2 }}/>
    <div className="absolute right-0 top-40 w-0.5 h-14 rounded-l-sm" style={{ background:'rgba(255,255,255,0.12)', right:-2 }}/>
    <div className="absolute left-0 top-20 w-0.5 h-8 rounded-r-sm" style={{ background:'rgba(255,255,255,0.12)', left:-2 }}/>
    <div className="absolute left-0 top-32 w-0.5 h-8 rounded-r-sm" style={{ background:'rgba(255,255,255,0.12)', left:-2 }}/>
    <div className="absolute rounded-[32px] pointer-events-none overflow-hidden inset-0"
      style={{ background:'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, transparent 50%)', borderRadius:32 }}/>
  </div>
);

// ─── Section ─────────────────────────────────────────────────────────────────
export const AiDebriefSection = () => {
  const sectionRef = useRef(null);
  const ipadRef    = useRef(null);
  const [visible, setVisible] = useState(false);
  const { lang } = useLanguage();
  const tx = t(lang).aiDebrief;
  const FEAT_ICONS = [Brain, Sparkles, ShieldCheck, BarChart2];

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: { trigger: sectionRef.current, start:'top 65%', once:true, onEnter:() => setVisible(true) }
      });
      tl.from('.deb-badge',   { opacity:0, y:20, duration:0.5 })
        .from('.deb-title',   { opacity:0, y:30, duration:0.6 }, '-=0.2')
        .from('.deb-feature', { opacity:0, x:-24, duration:0.5, stagger:0.1 }, '-=0.3')
        .from('.deb-kpis',    { opacity:0, y:20, duration:0.5 }, '-=0.2')
        .from(ipadRef.current, { opacity:0, x:60, rotateY:-8, duration:0.9, ease:'power3.out' }, 0.1);
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} id="ai-debrief" className="scroll-section min-h-screen flex items-center px-4 py-20">
      <div className="section-content max-w-[1280px] mx-auto w-full">
        <div className="grid gap-16 lg:grid-cols-2 items-end">

          {/* ── Left: Copy ── */}
          <div>
            <div className="deb-badge flex items-center gap-3 mb-8">
              <span className="w-8 h-px bg-[#F08D49]"/>
              <span className="font-mono text-xs text-[#F08D49] uppercase tracking-widest">{tx.label}</span>
            </div>

            <h2 className="deb-title font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4 text-white leading-tight">
              {tx.headline1}<br/>
              <span style={{ color:'#F08D49' }}>{tx.headline2}</span> {tx.headlineAccent}
            </h2>
            <p className="deb-title text-white/45 text-base mb-12 leading-relaxed max-w-md">
              {tx.sub}
            </p>

            <div className="space-y-6 mb-12">
              {tx.feats.map((item, i) => {
                const Icon = FEAT_ICONS[i];
                return (
                  <div key={i} className="deb-feature flex gap-4 items-start group">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110"
                      style={{ background:'rgba(240,141,73,0.1)', border:'1px solid rgba(240,141,73,0.2)' }}>
                      <Icon className="w-4 h-4" style={{ color:'#F08D49' }}/>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-white mb-0.5">{item.title}</h3>
                      <p className="text-sm text-white/50 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="deb-kpis">
              <KpiStrip cols={3} kpis={[
                { value:10,  suffix:'s', label: tx.kpi1 },
                { value:95,  suffix:'%', label: tx.kpi2 },
                { value:24,  suffix:'/7', label: tx.kpi3 },
              ]}/>
            </div>
          </div>

          {/* ── Right: iPad ── */}
          <div ref={ipadRef} className="flex justify-center lg:justify-end perspective-1000">
            <IPad visible={visible} lang={lang}/>
          </div>
        </div>
      </div>
    </section>
  );
};
